import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Popconfirm,
  Button,
  Space,
  Drawer,
  Row,
  Col,
  Upload,
} from "antd";
import type { UploadFile } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  getQuestionList,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  toggleQuestionUse,
  getIndicatorSelect,
  getCategoryList,
  getOptionList,
  createOption,
  updateOption,
  deleteOption,
  toggleOptionUse,
  uploadQuestionImages,
} from "../api/domainApi";
import { QuestionRes, QuestionImage, OptionRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const PAGE_SIZE = 20;
const AXIS_OPTS = [
  { value: "X", label: "X" },
  { value: "Y", label: "Y" },
  { value: "Z", label: "Z" },
];

const QuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [filterIndicatorId, setFilterIndicatorId] = useState<
    number | undefined
  >();
  const [filterCategoryId, setFilterCategoryId] = useState<
    number | undefined
  >();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<QuestionRes | null>(null);
  const [form] = Form.useForm();
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [selectedQuestion, setSelectedQuestion] = useState<QuestionRes | null>(
    null,
  );
  const [optDrawerOpen, setOptDrawerOpen] = useState(false);
  const [optModalOpen, setOptModalOpen] = useState(false);
  const [editingOpt, setEditingOpt] = useState<OptionRes | null>(null);
  const [optForm] = Form.useForm();

  const swrKey = `/question?page=${page}&kw=${keyword}&ind=${filterIndicatorId}&cat=${filterCategoryId}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () =>
      getQuestionList({
        page: page - 1,
        size: PAGE_SIZE,
        keyword,
        indicatorId: filterIndicatorId,
        categoryId: filterCategoryId,
      }),
    { keepPreviousData: true },
  );

  const { data: indicators } = useSWR("/indicator/select", getIndicatorSelect);
  const { data: categoriesData } = useSWR("/category", () =>
    getCategoryList({ size: 200 }),
  );
  const categories = categoriesData?.content;

  const optSwrKey = selectedQuestion
    ? `/question-option?qstId=${selectedQuestion.id}`
    : null;
  const { data: optData, mutate: mutateOpt } = useSWR(optSwrKey, () =>
    getOptionList({ questionId: selectedQuestion!.id }),
  );

  const openCreate = () => {
    setEditing(null);
    setPendingFiles([]);
    setImageRemoved(false);
    form.resetFields();
    setModalOpen(true);
  };
  const openEdit = (row: QuestionRes) => {
    setEditing(row);
    setPendingFiles([]);
    setImageRemoved(false);
    form.setFieldsValue({
      ...row,
      categoryIdList: row.categoryList?.map((c) => c.id),
    });
    setModalOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      let fileGrpId: number | null | undefined = values.fileGrpId;

      if (pendingFiles.length > 0) {
        setUploading(true);
        const rawFiles = pendingFiles.map((f) => f.originFileObj as File);
        const uploadRes = await uploadQuestionImages(rawFiles);
        fileGrpId = uploadRes.fileGrpId;
      } else if (imageRemoved) {
        fileGrpId = null;
      }

      const payload = { ...values, fileGrpId };

      if (editing) {
        await updateQuestion(editing.id, payload);
        message.success("수정되었습니다.");
      } else {
        await createQuestion(payload);
        message.success("추가되었습니다.");
      }
      setModalOpen(false);
      mutate();
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteQuestion(id);
      message.success("삭제되었습니다.");
      mutate();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const openOptDrawer = (row: QuestionRes) => {
    setSelectedQuestion(row);
    setOptDrawerOpen(true);
  };
  const openOptCreate = () => {
    setEditingOpt(null);
    optForm.resetFields();
    optForm.setFieldsValue({ questionId: selectedQuestion!.id });
    setOptModalOpen(true);
  };
  const openOptEdit = (row: OptionRes) => {
    setEditingOpt(row);
    optForm.setFieldsValue(row);
    setOptModalOpen(true);
  };

  const handleOptOk = async () => {
    const values = await optForm.validateFields();
    try {
      if (editingOpt) {
        await updateOption(editingOpt.id, values);
        message.success("수정되었습니다.");
      } else {
        await createOption(values);
        message.success("추가되었습니다.");
      }
      setOptModalOpen(false);
      mutateOpt();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleOptDelete = async (id: number) => {
    try {
      await deleteOption(id);
      message.success("삭제되었습니다.");
      mutateOpt();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const resetFilters = () => {
    setKwInput("");
    setKeyword("");
    setFilterIndicatorId(undefined);
    setFilterCategoryId(undefined);
    setPage(1);
  };

  const columns: ColumnsType<QuestionRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "지표", dataIndex: "indicatorName", width: 100 },
    { title: "제목", dataIndex: "title", ellipsis: { showTitle: true }, width: 200 },
    { title: "서브제목", dataIndex: "subTitle", ellipsis: { showTitle: true }, width: 200 },
    { title: "선택지", dataIndex: "optionCount", width: 70 },
    { title: "순서", dataIndex: "sortOrder", width: 60 },
    {
      title: "이미지",
      dataIndex: "images",
      width: 70,
      render: (images: QuestionImage[]) =>
        images?.length > 0 ? (
          <img
            src={images[0].fileUrl}
            alt={images[0].originalName}
            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "사용여부",
      dataIndex: "useYn",
      width: 90,
      render: (v) => <UseYnTag value={v} />,
    },
    {
      title: "관리",
      width: 300,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            수정
          </Button>
          <Button size="small" type="dashed" onClick={() => openOptDrawer(row)}>
            선택지
          </Button>
          <Button
            size="small"
            type="dashed"
            onClick={() => navigate(`/pro-courses/${row.id}`)}
          >
            프로코스
          </Button>
          <Button
            size="small"
            onClick={async () => {
              try {
                await toggleQuestionUse(row.id);
                mutate();
              } catch (e: any) {
                message.error(e.message);
              }
            }}
          >
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm
            title="삭제하시겠습니까?"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button size="small" danger>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const optColumns: ColumnsType<OptionRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "라벨", dataIndex: "label", width: 80 },
    { title: "내용", dataIndex: "content", ellipsis: { showTitle: true } },
    { title: "점수", dataIndex: "score", width: 70 },
    {
      title: "강제축",
      dataIndex: "forceAxis",
      width: 70,
      render: (v) => v ?? "-",
    },
    {
      title: "강제값",
      dataIndex: "forceAxisValue",
      width: 70,
      render: (v) => v ?? "-",
    },
    { title: "로직", dataIndex: "logicCount", width: 60 },
    {
      title: "사용여부",
      dataIndex: "useYn",
      width: 80,
      render: (v) => <UseYnTag value={v} />,
    },
    {
      title: "관리",
      width: 160,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openOptEdit(row)}>
            수정
          </Button>
          <Button
            size="small"
            onClick={async () => {
              try {
                await toggleOptionUse(row.id);
                mutateOpt();
              } catch (e: any) {
                message.error(e.message);
              }
            }}
          >
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제?" onConfirm={() => handleOptDelete(row.id)}>
            <Button size="small" danger>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="문항" onAdd={openCreate} />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="지표 선택"
          allowClear
          style={{ width: 180 }}
          options={indicators?.map((i) => ({ value: i.id, label: i.nameKo }))}
          onChange={(v) => {
            setFilterIndicatorId(v);
            setPage(1);
          }}
          value={filterIndicatorId}
        />
        <Select
          placeholder="카테고리 선택"
          allowClear
          style={{ width: 180 }}
          options={categories?.map((c) => ({ value: c.id, label: c.codeName }))}
          onChange={(v) => {
            setFilterCategoryId(v);
            setPage(1);
          }}
          value={filterCategoryId}
        />
        <Input
          placeholder="제목 검색"
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
          style={{ width: 220 }}
        />
        <Button
          onClick={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
        >
          검색
        </Button>
        <Button onClick={resetFilters}>초기화</Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      {/* Question Modal */}
      <Modal
        title={editing ? "문항 수정" : "문항 추가"}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        okText={editing ? "수정" : "추가"}
        cancelText="취소"
        confirmLoading={uploading}
        width={640}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="fileGrpId" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="indicatorId" label="행동지표">
            <Select
              allowClear
              options={indicators?.map((i) => ({
                value: i.id,
                label: i.nameKo,
              }))}
            />
          </Form.Item>
          <Form.Item label="이미지">
            {/* 기존 이미지: 제거 안 했고 새 파일 선택 안 한 경우 */}
            {!imageRemoved && pendingFiles.length === 0 && editing?.images && editing.images.length > 0 && (
              <Space wrap style={{ marginBottom: 8 }}>
                {editing.images.map((img) => (
                  <img
                    key={img.fileId}
                    src={img.fileUrl}
                    alt={img.originalName}
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: "cover",
                      border: "1px solid #d9d9d9",
                      borderRadius: 4,
                    }}
                  />
                ))}
              </Space>
            )}
            {/* 새 파일 선택 시 즉시 미리보기 */}
            {pendingFiles.length > 0 && (
              <Space wrap style={{ marginBottom: 8 }}>
                {pendingFiles.map((f) => (
                  <img
                    key={f.uid}
                    src={f.originFileObj ? URL.createObjectURL(f.originFileObj) : f.thumbUrl}
                    alt={f.name}
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: "cover",
                      border: "1px solid #1677ff",
                      borderRadius: 4,
                    }}
                  />
                ))}
              </Space>
            )}
            {/* 제거 상태 안내 */}
            {imageRemoved && pendingFiles.length === 0 && (
              <div style={{ marginBottom: 8, color: "#ff4d4f", fontSize: 12 }}>
                저장 시 이미지가 제거됩니다
              </div>
            )}
            <Space>
              <Upload
                fileList={pendingFiles}
                beforeUpload={() => false}
                onChange={({ fileList }) => {
                  setPendingFiles(fileList);
                  setImageRemoved(false);
                }}
                accept="image/*"
                multiple
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>
                  {editing?.images?.length && !imageRemoved ? "이미지 교체" : "이미지 업로드"}
                </Button>
              </Upload>
              {pendingFiles.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setPendingFiles([])}
                >
                  선택 취소
                </Button>
              )}
              {!imageRemoved && editing?.images && editing.images.length > 0 && pendingFiles.length === 0 && (
                <Button
                  size="small"
                  danger
                  onClick={() => setImageRemoved(true)}
                >
                  이미지 제거
                </Button>
              )}
              {imageRemoved && (
                <Button
                  size="small"
                  onClick={() => setImageRemoved(false)}
                >
                  제거 취소
                </Button>
              )}
            </Space>
          </Form.Item>
          <Form.Item name="title" label="제목" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="subTitle"
            label="서브 제목"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="purpose" label="의도">
            <Input />
          </Form.Item>
          <Form.Item name="categoryIdList" label="카테고리">
            <Select
              mode="multiple"
              options={categories?.map((c) => ({
                value: c.id,
                label: c.codeName,
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="extraCode" label="별도질문 코드">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="extraLabel" label="결과 라벨">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="정렬 순서"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="extraShowYn"
                label="결과 노출"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Options Drawer */}
      <Drawer
        title={`선택지 — ${selectedQuestion?.title ?? ""}`}
        open={optDrawerOpen}
        onClose={() => setOptDrawerOpen(false)}
        size={900}
        extra={
          <Button type="primary" onClick={openOptCreate}>
            선택지 추가
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={optColumns}
          dataSource={optData}
          pagination={false}
          size="small"
        />
      </Drawer>

      {/* Option Modal */}
      <Modal
        title={editingOpt ? "선택지 수정" : "선택지 추가"}
        open={optModalOpen}
        onOk={handleOptOk}
        onCancel={() => setOptModalOpen(false)}
        okText={editingOpt ? "수정" : "추가"}
        cancelText="취소"
      >
        <Form form={optForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="questionId" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="content" label="내용" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="label" label="라벨" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="score" label="점수" rules={[{ required: true }]}>
                <InputNumber step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="정렬 순서"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="forceAxis" label="강제 축 (선택)">
                <Select allowClear options={AXIS_OPTS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="forceAxisValue" label="강제 축 값 (1~3)">
                <InputNumber style={{ width: "100%" }} min={1} max={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default QuestionPage;
